import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VariableSizeList } from 'react-window';
import type { ListChildComponentProps } from 'react-window';
import type { RunTraceStatus } from '@/types/api';
import { formatTime } from '@/utils/format';
import { ARG_SEPARATOR, invocationLabel, rowKey, summaryParts } from '@/utils/traceRows';


const ROW_HEIGHT = 32;
const ARG_LINE_HEIGHT = 18;
const HEADING_HEIGHT = 26;
const LIST_HEIGHT = 320;

export interface LiveActivityRow {
  stage_id: string;
  invocation_key: string;
  turn: number;
  tool_names: ReadonlyArray<string>;
  summary: string;
  ts?: string | undefined;
}

import { ROLE_ICON, STAGE_ROLE, stageTintClasses } from '@/utils/roleTheme';

export const ACTIVITY_STAGES: ReadonlyArray<string> = [
  'explore',
  'plan',
  'code',
  'review',
  'test',
  'document'
];

export const ALL_STAGES = '__all__';

export interface LiveActivityPanelProps {
  rows: ReadonlyArray<LiveActivityRow>;
  current?: { tool: string; args: string; turn: number } | undefined;
  status: RunTraceStatus | 'fetch_failed';
  isLive: boolean;
  stageId?: string | undefined;
  elapsed?: string | undefined;
  stopControl?: React.ReactNode;
  activeStage?: string | undefined;
  onSelectStage?: ((stage: string) => void) | undefined;
  stageStates?: Readonly<Record<string, string>> | undefined;
}

export function collapseToolNames(names: ReadonlyArray<string>): string {
  if (names.length === 0) return '';
  const counts = new Map<string, number>();
  for (const n of names) counts.set(n, (counts.get(n) ?? 0) + 1);
  return [...counts.entries()]
    .map(([n, c]) => (c > 1 ? `${n} ×${c}` : n))
    .join(ARG_SEPARATOR);
}


function EmptyState({
  status,
  isLive,
  stage,
  stageState
}: {
  status: LiveActivityPanelProps['status'];
  isLive: boolean;
  stage?: string | undefined;
  stageState?: string | undefined;
}): JSX.Element {
  const perStage =
    stage !== undefined && stage !== ALL_STAGES
      ? stageState === undefined || stageState === 'pending'
        ? `The ${stage} stage has not run yet.`
        : stageState === 'skipped'
          ? `The ${stage} stage was skipped — it had nothing to do.`
          : stageState === 'running'
            ? `The ${stage} stage is running; no tool activity yet.`
            : `The ${stage} stage recorded no tool activity.`
      : undefined;

  const text =
    status === 'reclaimed'
      ? 'Run completed and delivered. The activity trace was reclaimed with the workspace.'
      : status === 'tracing_disabled'
        ? 'Activity tracing is not enabled for this run.'
        : status === 'fetch_failed'
          ? 'Could not load the activity trace.'
          : (perStage ??
            (isLive
              ? "Waiting for the agent's first activity…"
              : 'No activity was recorded for this run.'));
  return (
    <div
      className="px-1 py-4 text-[12px] text-text-muted"
      data-testid="live-activity-empty"
      data-empty-reason={status}
    >
      {text}
    </div>
  );
}

export function LiveActivityPanel({
  rows,
  current,
  status,
  isLive,
  stageId,
  elapsed,
  stopControl,
  activeStage,
  onSelectStage,
  stageStates
}: LiveActivityPanelProps): JSX.Element {
  const stage = activeStage ?? ALL_STAGES;

  const countsByStage = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of rows) m[r.stage_id] = (m[r.stage_id] ?? 0) + 1;
    return m;
  }, [rows]);

  const visible = useMemo(
    () =>
      rows
        .filter((r) => stage === ALL_STAGES || r.stage_id === stage)
        .sort((a, b) => {
          if (a.ts !== undefined && b.ts !== undefined && a.ts !== b.ts) {
            return a.ts < b.ts ? 1 : -1;
          }
          if (a.ts === undefined && b.ts !== undefined) return 1;
          if (a.ts !== undefined && b.ts === undefined) return -1;
          return a.invocation_key === b.invocation_key
            ? b.turn - a.turn
            : b.invocation_key.localeCompare(a.invocation_key);
        }),
    [rows, stage]
  );

  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const listRef = useRef<VariableSizeList>(null);

  const partsFor = useCallback((r: LiveActivityRow): string[] => summaryParts(r.summary), []);

  const startsInvocation = useCallback(
    (index: number): boolean => {
      const r = visible[index];
      if (!r) return false;
      const prev = visible[index - 1];
      return prev === undefined || prev.invocation_key !== r.invocation_key;
    },
    [visible]
  );

  const itemSize = useCallback(
    (index: number): number => {
      const r = visible[index];
      if (!r) return ROW_HEIGHT;
      const heading = startsInvocation(index) ? HEADING_HEIGHT : 0;
      if (!expanded.has(rowKey(r))) return ROW_HEIGHT + heading;
      const parts = partsFor(r);
      return ROW_HEIGHT + heading + Math.max(0, parts.length - 1) * ARG_LINE_HEIGHT;
    },
    [visible, expanded, partsFor, startsInvocation]
  );

  useEffect(() => {
    listRef.current?.resetAfterIndex(0, true);
  }, [expanded, visible]);

  const toggle = useCallback((key: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const Row = ({ index, style }: ListChildComponentProps): JSX.Element => {
    const r = visible[index]!;
    const key = rowKey(r);
    const parts = partsFor(r);
    const isMulti = parts.length > 1;
    const isOpen = expanded.has(key);
    const tools = collapseToolNames(r.tool_names);
    const heading = startsInvocation(index) ? invocationLabel(r.invocation_key) : undefined;

    return (
      <div style={style}>
        {heading ? (
          <div
            className="flex items-center gap-2 h-[26px] px-4 text-[11px] font-medium text-text-muted"
            data-testid={`live-invocation-heading-${r.invocation_key}`}
          >
            <span className="h-px flex-none w-3 bg-border" />
            <span className="truncate">{heading}</span>
            <span className="h-px flex-1 bg-border" />
          </div>
        ) : null}
        <div
          className="mk-turn grid grid-cols-[52px_66px_210px_1fr] gap-3 px-4 text-[12px]"
          data-testid={`live-turn-row-${r.turn}`}
          data-expanded={isMulti ? (isOpen ? 'true' : 'false') : undefined}
        >
        <span className="font-mono text-text-muted/60 tabular-nums leading-[32px] text-[11px]">
          {r.turn}
        </span>
        <span
          className="font-mono text-text-muted tabular-nums leading-[32px] text-[11px]"
          data-testid={`live-turn-time-${r.turn}`}
          title={r.ts ?? undefined}
        >
          {formatTime(r.ts)}
        </span>
        <span className="leading-[32px] truncate flex items-center gap-1.5" title={tools}>
          {tools ? (
            <span className="event-type truncate !rounded-full self-center leading-[16px]">
              {tools}
            </span>
          ) : null}
        </span>
        {isMulti ? (
          <button
            type="button"
            onClick={() => toggle(key)}
            aria-expanded={isOpen}
            data-testid={`live-turn-toggle-${r.turn}`}
            className="text-left font-mono text-[11.5px] text-text min-w-0"
            title={r.summary}
          >
            <span className="block leading-[32px] truncate">
              <span className="text-text-muted mr-1">{isOpen ? '▾' : '▸'}</span>
              {isOpen ? parts[0] : r.summary}
            </span>
            {isOpen
              ? parts.slice(1).map((p, i) => (
                  <span
                    key={`${key}-arg-${i + 1}`}
                    className="block truncate pl-4 leading-[18px]"
                    data-testid={`live-turn-arg-${r.turn}`}
                  >
                    {p}
                  </span>
                ))
              : null}
          </button>
        ) : (
          <span
            className="font-mono text-[11.5px] text-text truncate leading-[32px]"
            title={r.summary}
          >
            {r.summary}
          </span>
        )}
        </div>
      </div>
    );
  };

  return (
    <section
      className="bg-panel border border-border rounded-lg p-4 mb-4"
      data-testid="live-activity"
      aria-label="Live agent activity"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <h3 className="font-semibold text-[13px]">Agent activity</h3>
          {isLive ? (
            <span
              className="text-warn text-[13px] font-semibold cursor-help leading-none shrink-0"
              data-testid="live-retention-note"
              tabIndex={0}
              role="note"
              aria-label="Working files are temporary — this activity trace lives with the run's workspace and is tidied away once the pipeline completes successfully."
              title="Working files are temporary — this activity trace lives with the run's workspace and is tidied away once the pipeline completes successfully."
            >
              ⓘ
            </span>
          ) : null}
          {stageId ? (
            <span
              className={`font-mono text-[11px] px-2 py-0.5 rounded-full ${stageTintClasses(stageId)}`}
              data-testid="stage-chip"
            >
              {stageId}
            </span>
          ) : null}
          {current ? (
            <span className="font-mono text-[11px] text-text-muted" data-testid="live-turn">
              turn {current.turn}
            </span>
          ) : null}
          {elapsed ? (
            <span className="font-mono text-[11px] text-text-muted tabular-nums" data-testid="live-elapsed">
              {elapsed}
            </span>
          ) : null}
        </div>
        {stopControl}
      </div>

      {onSelectStage ? (
        <nav
          className="flex flex-wrap gap-1 mb-3 border-b border-border"
          role="tablist"
          aria-label="Activity by stage"
        >
          {[ALL_STAGES, ...ACTIVITY_STAGES].map((id) => {
            const isAll = id === ALL_STAGES;
            const count = isAll ? rows.length : (countsByStage[id] ?? 0);
            const ico = ROLE_ICON[STAGE_ROLE[id] ?? ''];
            const selected = stage === id;
            const dimmed =
              !isAll && count === 0 && (stageStates?.[id] ?? 'pending') === 'pending';
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => onSelectStage(id)}
                data-testid={`activity-tab-${isAll ? 'all' : id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[12px] -mb-px border-b-2 ${
                  selected
                    ? 'text-text border-text font-medium'
                    : 'text-text-muted border-transparent hover:text-text'
                } ${dimmed ? 'opacity-50' : ''}`}
              >
                {ico ? (
                  <span
                    className={`w-[16px] h-[16px] rounded-[4px] grid place-items-center font-bold text-[8px] flex-none ${ico.bg} ${ico.fg}`}
                  >
                    {ico.abbr}
                  </span>
                ) : null}
                <span>{isAll ? 'All streaming events' : id}</span>
                {count > 0 ? (
                  <span className="font-mono text-[10px] text-text-muted tabular-nums">
                    {count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </nav>
      ) : null}

      {current !== undefined || isLive || visible.length > 0 ? (
        <div
          className="bg-panel-2 border border-border rounded-md px-3 py-2 mb-2 flex items-center justify-between gap-3 min-h-[34px]"
          data-testid="live-status-strip"
        >
          <p
            className="text-[12px] text-text flex items-center gap-2 min-w-0"
            aria-live="polite"
            data-testid="live-current-step"
          >
            {current ? (
              <>
                <span className="w-1.5 h-1.5 rounded-full bg-ok animate-pulse flex-none" />
                <span className="font-mono truncate" data-testid="live-current-tool">
                  {current.tool}
                </span>
                {current.args ? (
                  <span className="text-text-muted truncate" data-testid="live-current-args">
                    {current.args}
                  </span>
                ) : null}
              </>
            ) : isLive ? (
              <>
                <span className="w-1.5 h-1.5 rounded-full bg-text-muted/50 flex-none" />
                <span className="text-text-muted" data-testid="live-hint">
                  waiting for the next step…
                </span>
              </>
            ) : null}
          </p>

          {visible.length > 0 ? (
            <span
              className="text-[11px] text-text-muted whitespace-nowrap flex-none"
              data-testid="live-turn-list-hint"
            >
              {isLive ? 'newest first' : 'completed before the run ended · newest first'}
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="bg-panel-2 border border-border rounded-lg px-3 py-1 overflow-hidden">
        {visible.length === 0 ? (
          <EmptyState
            status={status}
            isLive={isLive}
            stage={stage}
            stageState={stage === ALL_STAGES ? undefined : stageStates?.[stage]}
          />
        ) : (
          <div data-testid="live-turn-list">
            <VariableSizeList
              ref={listRef}
              height={Math.min(
                LIST_HEIGHT,
                Math.max(ROW_HEIGHT, visible.reduce((h, _r, i) => h + itemSize(i), 0))
              )}
              itemCount={visible.length}
              itemSize={itemSize}
              width="100%"
            >
              {Row}
            </VariableSizeList>
          </div>
        )}
      </div>
    </section>
  );
}
